import { useEffect } from 'react';
import {
    Navigate, generatePath, useLocation, useParams,
} from 'react-router-dom';
import { getLocalStorage, clearLocalStorage } from 'helpers/localStorage/helpers';
import { PageSpecificKeys } from 'helpers/localStorage';
import { SignUpStage } from 'views/public/SignUp/SignUpContext';
import { useGetProForma } from 'queries/ProForma';
import { useGetUploadedProForma } from 'queries/UploadedProForma';
import useAuthContext from 'contexts/AuthContext';
import { AxiosError } from 'axios';
import { faTriangleExclamation } from '@fortawesome/pro-solid-svg-icons';
import useGetDealOrganization from 'views/Deals/hooks/useGetDealOrganization';
import ErrorPage from 'components/ErrorPage/ErrorPage';
import { PAGE_NOT_FOUND_MESSAGE, PRO_FORMA_NOT_FOUND_MESSAGE } from 'components/ErrorPage/constants';
import { URLS } from './services/urls';

const AppRouterRedirectHandler = ({ children }: { children: JSX.Element }) => {
    const location = useLocation();
    const { dealId, proFormaId } = useParams();
    const signUpStage = getLocalStorage(PageSpecificKeys.SignUpStage);
    const {
        selectedOrganizationId, handleUpdateSelectedOrganization, 
        userOrganizations, isLoadingUserOrganizations,
        isFirstDealLogin, exampleDealId,
    } = useAuthContext();

    const {
        data: dealOrganization,
        error: dealOrgError,
        isLoading: isDealOrgLoading,
    } = useGetDealOrganization({
        dealId: Number(dealId),
        options: {
            retry: false,
            refetchOnWindowFocus: false,
        },
    });

    useEffect(() => {
        if (dealOrganization && dealOrganization.correlationId !== selectedOrganizationId) {
            const hasAccessToOrg = userOrganizations.some(
                org => org.organization.id === dealOrganization.id,
            );

            if (hasAccessToOrg) {
                handleUpdateSelectedOrganization(dealOrganization.id);
            }
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dealOrganization?.correlationId, selectedOrganizationId, userOrganizations, handleUpdateSelectedOrganization]);

    const isUploadPath = location.pathname.includes('upload');

    const { data: proForma, error: proFormaError } = useGetProForma(Number(dealId), Number(proFormaId), {
        enabled: !!dealId && !!proFormaId && !isDealOrgLoading && !dealOrgError && !isUploadPath,
    });

    const { error: uploadedProFormaError } = useGetUploadedProForma(
        selectedOrganizationId!, Number(dealId), Number(proFormaId), {
            enabled: !!dealId && !!proFormaId && !!selectedOrganizationId
                && !isDealOrgLoading && !dealOrgError && isUploadPath,
        },
    );

    // Signup redirect takes priority
    let redirectPath: string | undefined;
    if (signUpStage === SignUpStage.ACCOUNT_CREATED) {
        redirectPath = URLS.ACCOUNT_CREATED;
    } else if (signUpStage === SignUpStage.DIRECT_SIGN_UP_COMPLETED) {
        redirectPath = URLS.DEALS.HOME;
        clearLocalStorage(PageSpecificKeys.SignUpStage);
        clearLocalStorage(PageSpecificKeys.SignUpData);
        clearLocalStorage(PageSpecificKeys.IsDirectSignUpFlow);
    }

    if (redirectPath && location.pathname !== redirectPath) {
        return <Navigate to={redirectPath} replace />;
    }

    // First-time Deal user: redirect to example deal overview
    if (isFirstDealLogin && exampleDealId) {
        const exampleDealPath = `/deal/${exampleDealId}`;
        if (!location.pathname.startsWith(exampleDealPath)) {
            return <Navigate to={exampleDealPath} replace />;
        }
    }

    // Deal not found or user has no access to the deal's organization
    if (dealId && dealOrgError) {
        const axiosError = dealOrgError as AxiosError;
        if (axiosError.response?.status === 404) {
            return (
                <ErrorPage
                    message={PAGE_NOT_FOUND_MESSAGE}
                    icon={faTriangleExclamation}
                />
            );
        }
        if (axiosError.response?.status === 403) {
            return <ErrorPage />;
        }
    }

    // Deal org loaded but user doesn't belong to that organization.
    // Wait for user_organizations too — otherwise the empty placeholder array
    // would falsely trigger ErrorPage when dealOrganization resolves first.
    if (dealId && dealOrganization && !isLoadingUserOrganizations
        && !userOrganizations.some(org => org.organization.id === dealOrganization.id)) {
        return <ErrorPage />;
    }

    // Pro forma doesn't belong to the deal or user has no access
    if (proFormaId && proFormaError) {
        const error = proFormaError as AxiosError;
        if (error.response?.status === 403) {
            return <ErrorPage />;
        }
        if (error.response?.status === 404 || proForma?.dealId !== Number(dealId)) {
            return (
                <ErrorPage
                    message={PRO_FORMA_NOT_FOUND_MESSAGE}
                    buttonText="Back to Deal"
                    buttonUrl={generatePath(URLS.PRO_FORMA.HOME, { dealId })}
                    icon={faTriangleExclamation}
                />
            );
        }
    }

    if (proFormaId && uploadedProFormaError) {
        const error = uploadedProFormaError as AxiosError;
        if (error.response?.status === 403) {
            return <ErrorPage />;
        }
        if (error.response?.status === 404) {
            return (
                <ErrorPage
                    message={PRO_FORMA_NOT_FOUND_MESSAGE}
                    buttonText="Back to Deal"
                    buttonUrl={generatePath(URLS.PRO_FORMA.HOME, { dealId })}
                    icon={faTriangleExclamation}
                />
            );
        }
    }

    return children;
};

export default AppRouterRedirectHandler;
